import type { MenuCategory, MenuProduct } from '../model/schema';

/** A category paired with its products, ready for the catalog to render. */
export interface CategoryGroup {
  category: MenuCategory;
  products: MenuProduct[];
}

/**
 * The synthetic category that collects products whose own category is gone.
 * Not a real taxonomy row — it never reaches a write, and the catalog renders
 * it without the rename/delete actions a real category carries.
 */
export const UNCATEGORIZED_ID = '__uncategorized__';

/**
 * Group a flat product list under its categories, preserving category order.
 * Categories with no products are still included (they render an empty section
 * header), so a freshly created category is visible before its first product.
 *
 * Products whose `categoryId` matches NO category are collected into a trailing
 * synthetic group rather than dropped. The wire genuinely serves such rows: a
 * deleted category leaves its products behind pointing at an id that
 * `product-categories/by-merchant` no longer returns (verified against
 * test.api.noor.uz — those ids answer `4001 product category not found`).
 *
 * Dropping them silently was worse than it sounds: the merchant could not see,
 * edit or re-file the product, and a create landing in such a category looked
 * exactly like a failed save — the request succeeded and nothing appeared.
 */
export function groupProductsByCategory(
  categories: MenuCategory[],
  products: MenuProduct[],
  /** Label for the synthetic group; omitted in contexts with no translator. */
  uncategorizedLabel = 'Без категории',
): CategoryGroup[] {
  // Compared as strings: both ids are `string | number` on the view model, so
  // strict equality would orphan every product of a numerically-typed category.
  // The orphan check below uses the same comparison, so a product can never be
  // both unclaimed here and claimed there.
  const groups = categories.map((category) => ({
    category,
    products: products.filter(
      (product) => String(product.categoryId) === String(category.id),
    ),
  }));

  const known = new Set(categories.map((category) => String(category.id)));
  const orphans = products.filter((product) => !known.has(String(product.categoryId)));
  if (orphans.length === 0) return groups;

  return [
    ...groups,
    { category: { id: UNCATEGORIZED_ID, name: uncategorizedLabel }, products: orphans },
  ];
}

/**
 * Which category a NEW product should start in, given the category the merchant
 * is standing in. `group` is the category whose own "add" action was used;
 * `active` is the one the screen is currently showing (the mobile chip rail's
 * selection, or desktop's sidebar row) for the adds that carry no group — the
 * floating button and the empty state.
 *
 * Returns `undefined` when there is nothing safe to pre-select, which the
 * payload spreads away so the wizard opens with an empty category select.
 * The synthetic uncategorized bucket is deliberately NOT pre-selectable: it is
 * not a real taxonomy row, so seeding it would put an id in the select that
 * every write rejects.
 */
export function preselectedCategoryId(
  group: CategoryGroup | undefined,
  active: MenuCategory['id'] | undefined,
): MenuCategory['id'] | undefined {
  const candidate = group ? group.category.id : active;
  if (candidate === undefined || candidate === UNCATEGORIZED_ID) return undefined;
  return candidate;
}

/**
 * Drop categories left with no products, for use when a filter is active.
 *
 * {@link groupProductsByCategory} deliberately KEEPS empty categories: that is
 * how a freshly created category is visible before its first product. Under a
 * search or a moderation pill that same rule is wrong — the merchant is asking
 * "where is this product", and answering with every category they own, each
 * captioned "В этой категории пока нет продуктов", buries the one match among
 * the empty headers.
 *
 * A no-op when nothing is filtered, so the caller can apply it unconditionally
 * and pass the flag rather than branching around it.
 */
export function dropEmptyGroups(
  groups: CategoryGroup[],
  isFiltered: boolean,
): CategoryGroup[] {
  if (!isFiltered) return groups;
  return groups.filter((group) => group.products.length > 0);
}
